import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { title: "", type: "monthly", date: "", description: "", image_file_id: null, published: true };

export default function AdminResults() {
  useSEO("Admin · Results");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/results").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/results/${editId}`, form);
      else await api.post("/admin/results", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/results/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="Result Reports" subtitle="Upload weekly & monthly performance reports"
        action={<Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="gradient-brand text-brand-navy font-semibold" data-testid="new-result-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Report</Button>} />
      <p className="text-xs text-amber-400 mb-4">Note: Past performance does not guarantee future results.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 ? <p className="text-slate-500 col-span-full">No reports yet.</p>
          : rows.map((r) => (
            <div key={r.id} className="card-surface rounded-2xl overflow-hidden" data-testid={`admin-result-${r.id}`}>
              {r.image_file_id && <img src={fileUrl(r.image_file_id)} alt={r.title} className="w-full h-36 object-cover" />}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-mono uppercase text-brand-turquoise">{r.type} · {r.date}</span>
                  <div className="flex gap-1">
                    <button onClick={() => { setForm({ ...empty, ...r }); setEditId(r.id); setOpen(true); }} className="text-slate-500 hover:text-brand-cyan"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(r.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-result-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="mt-1 font-display font-semibold text-white">{r.title}</p>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "Add"} Report</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <ImageUpload value={form.image_file_id} onChange={(v) => setForm({ ...form, image_file_id: v })} label="Report Image" />
            <div><Label className="text-slate-300">Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="result-title" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="result-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-slate-300">Date</Label><Input required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="2026-06 or Jun 2026" data-testid="result-date" className="mt-1.5 bg-white/5 border-white/10" /></div>
            </div>
            <div><Label className="text-slate-300">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="result-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
